/**
 * A KÉZI NAV-SZINKRON — „szinkronizálj most", a palettáról.
 *
 * MIÉRT NINCS GOMBJA A HIÁNYLISTÁN. A `CLAUDE.md` §2 szerint a szinkronizálás
 * MŰVELET, NEM OLDAL, és a `commands.ts` fejléce ki is mondja, hogy a paletta
 * „az a hely, ahova a ritka műveletek mennek". Egy külön gomb minden
 * cégcsoport fejlécében a bekérés mellé kerülne — az `admin-egyszerusites.md`
 * pont az ilyen halmozódás ellen szól, és a napi munkában ezt a műveletet
 * ritkán kell elővenni: az ütemező magától fut.
 *
 * ⚠️ AMIT A VÁLASZ JELENT, ÉS AMIT NEM. `202 Accepted`: a cégek a SORON
 * vannak, nem lefutottak. A NAV-lekérdezés lapoz, a sor `concurrency: 1`-gyel
 * halad, a forgalomkorlát pedig a mi IP-nkre globális — nyolcvan cég percek.
 *
 * ═══ A KÖVETÉS (#37) — ÉS AMIT EZ FELÜLÍR ═══
 *
 * Eddig itt ez állt: *„Egy »kész« felirat itt hazugság volna, egy magától
 * frissülő lista pedig annál rosszabb: úgy nézne ki, mintha már látszana az
 * eredmény."* Ez IGAZ VOLT, amíg nem tudtuk, mikor van kész — és pontosan
 * ezért kellett a könyvelőre bízni a frissítést.
 *
 * A `GET /nav/sync/status` óta tudjuk. A tilalom tehát nem oldódik fel, hanem
 * PONTOSODIK: azt továbbra sem mondjuk, hogy „kész", amíg nem az, és a listát
 * továbbra sem frissítjük menet közben. De amikor a kör VALÓBAN lefutott, a
 * frissítés nem hazugság, hanem az egyetlen helyes lépés — a régi mondat
 * („utána frissítsd a listát") egy olyan teendőt adott a könyvelőnek, amit a
 * rendszer tud elvégezni. *(Viktor, 2026-09-15.)*
 *
 * ⚠️ A NEVEZŐ ITT VAN, NEM A SZERVEREN. A „7 kész" annyiból van, amennyit a
 * `202` sorba tett (`queued`) — a szerver egy későbbi lekérdezésből ezt nem
 * tudja visszafejteni, mert a cégsor közben változhatott. A levezetés a
 * backend `nav-sync-progress.ts` fejlécében áll.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';
import {
  EMPTY_MEMORY,
  etaSecondsOf,
  type FollowMemory,
  followStep,
  type NavSyncProgress,
  remainingOf,
} from './navSyncFollow';

// A KÖVETÉS SZABÁLYA ÉS A VÁLASZ ALAKJA A `navSyncFollow.ts`-BEN ÁL, tiszta
// függvényként — ott mérhető. Innen csak újra kiajánljuk, hogy a hívóknak
// (`NavSyncNotice.tsx`) egy importja legyen.
export type { NavSyncProgress } from './navSyncFollow';

/** Amit a `POST /nav/sync` válaszol — a backend `ManualSyncResult`-ja. */
export interface NavSyncResult {
  /** Ennyi cég került a sorra. */
  queued: number;
  /** Ennyinél a sorba tétel maga hasalt el — részleges siker, nem hiba. */
  failed: number;
  /** Ennyinél nincs bekötve a NAV technikai felhasználója. */
  notConnected: number;
  /** A kör indulása a SZERVER órájáról, ISO-8601 — ez a követés horgonya. */
  startedAt: string;
}

/**
 * A kimenetek.
 *
 * A két hibaág külön van, ugyanazzal az érveléssel, mint a hiánylistánál: a
 * `forbidden` ellen az újrapróbálás nem segít, emberhez kell fordulni; a
 * `failed` viszont lehet átmeneti.
 *
 * ⚠️ A `stalled` NEM HIBA, ÉS EZÉRT NEM A `failed` EGYIK ESETE. Azt jelenti,
 * hogy a kör tovább tart, mint amennyit értelmes várni — a munka a soron
 * HALAD, csak mi nem várunk rá tovább. Egy „nem sikerült" felirat itt hazudna,
 * és a könyvelő újraindítaná azt, ami épp fut.
 */
export type NavSyncStatus =
  | 'idle'
  | 'sending'
  | 'running'
  | 'done'
  | 'stalled'
  | 'failed'
  | 'forbidden';

export interface NavSyncState {
  status: NavSyncStatus;
  result: NavSyncResult | null;
  progress: NavSyncProgress | null;
  /** Hány cég van még hátra ebből a körből. A sáv nevezője a `result.queued`. */
  remaining: number;
  /** Becsült hátralévő idő másodpercben, vagy `null`, ha nincs mire alapozni. */
  etaSeconds: number | null;
  run: () => void;
  /** A visszajelzés eltüntetése — a könyvelő nyugtázta. */
  dismiss: () => void;
}

/**
 * MILYEN SŰRŰN KÉRDEZÜNK.
 *
 * Három másodperc: elég ritka ahhoz, hogy egy percekig tartó körben ne legyen
 * érezhető terhelés (két olcsó, indexelt lekérdezés), és elég sűrű ahhoz, hogy
 * a sáv MOZOGJON. Egy tízmásodperces ütem mellett egy gyors, négycéges kör
 * egyetlen ugrásban érne véget — abból a könyvelő nem azt tanulja meg, hogy
 * halad, hanem azt, hogy nem történik semmi.
 */
const POLL_MS = 3_000;

/**
 * MEDDIG VÁRUNK ÖSSZESEN.
 *
 * Nyolcvan cég a mért ütemben bőven belefér; ami ezen túl van, az már nem
 * várakozás, hanem üzemzavar — és arra nem a sáv a válasz, hanem a napló. A
 * határon NEM hazudunk készet: `stalled` lesz belőle, ami kimondja, hogy a
 * munka megy tovább nélkülünk.
 */
const MAX_WAIT_MS = 20 * 60_000;

/**
 * MEDDIG VÁRUNK EGY MEG SEM MOZDULT KÖRRE.
 *
 * Rövidebb a fentinél, mert itt nincs mit nézni. Nem rövidebb viszont
 * néhány percnél: EGY sor van az egész rendszerben (`concurrency: 1`), és a
 * kör mögé kerülhet egy futó OCR-kinyerés — a percekben mért várakozás ott
 * normális üzem, nem baj. A levezetés a `navSyncFollow.ts` fejlécében áll.
 */
const COLD_WAIT_MS = 3 * 60_000;

/**
 * ENNYI MOZDULATLAN KÖR UTÁN TEKINTJÜK LEFUTOTTNAK.
 *
 * ⚠️ MIÉRT KELL EGYÁLTALÁN, AMIKOR VAN NEVEZŐ. Mert a nevező felső becslés: a
 * `queued` minden bekötött céget beleszámol, a munkás viszont kihagyja azt,
 * amelyiknek nincs mit lekérdeznie — olyankor `SyncRun` sem születik
 * (`nav-sync.job.ts`: „NINCS MIT KÉRDEZNI"). Az a cég tehát SOHA nem kerül át
 * a „kész" oldalra, és nevező-figyeléssel a sáv örökre 9/12-n állna.
 *
 * Négy kör tizenkét másodperc teljes csendet jelent: se új futás, se új lap,
 * és semmi nem fut éppen. Rövidebb ablak egy lassú cég két lapja között is
 * eltelhet.
 */
const QUIET_POLLS = 4;

/** Ennyi egymás utáni hibás lekérdezés után hagyjuk abba a követést. */
const MAX_POLL_ERRORS = 3;

/**
 * @param onFinished A kör lefutása után hívjuk — a listák újratöltése.
 *   ⚠️ CSAK A `done` ÁGON. A `stalled` azt jelenti, hogy a munka MÉG MEGY;
 *   egy frissítés ott félkész állapotot mutatna késznek.
 */
export function useNavSync(onFinished?: () => void): NavSyncState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<NavSyncStatus>('idle');
  const [result, setResult] = useState<NavSyncResult | null>(null);
  const [progress, setProgress] = useState<NavSyncProgress | null>(null);

  /**
   * ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — és ez nem stílus. Egy
   * `setStatus(current => …)` frissítőjében indított kérés `StrictMode` alatt
   * KÉTSZER futna le: a React szándékosan kétszer hívja meg a frissítőt, hogy
   * kiderüljön, tiszta függvény-e. Az nem az: hálózati kérést indítana.
   */
  const inFlight = useRef(false);

  /**
   * A VISSZAHÍVÁS REFERENCIÁN UTAZIK, nem a követő effekt függőségei közt.
   *
   * A hívó (`App.tsx`) minden renderben új függvényt adna át (két `reload`
   * összefűzése), és egy függőségként bekötött visszahívás a KÖVETÉST
   * indítaná újra minden renderben — az időzítő nullázódna, és a lekérdezés
   * soha nem érne el a következő körig.
   */
  const finished = useRef(onFinished);
  // ⚠️ EFFEKTBEN, NEM A RENDERBEN. Egy renderfázisú ref-írás működik, de a
  // React nem garantálja: egy ELDOBOTT render is mutálná. Függőség nélküli
  // effekt, tehát minden render után lefut — a kezdőértéket a `useRef` adja,
  // így az első kör sem marad visszahívás nélkül. *(Code review, 09-15.)*
  useEffect(() => {
    finished.current = onFinished;
  });

  const run = useCallback(() => {
    // A MÁSODIK KATTINTÁS NEM INDÍT MÁSODIK KÖRT, amíg az első úton van. A
    // szerver is dedupál (egyperces feladat-azonosító), de ez a felület
    // őszinteségéről szól: két egymás utáni „sorba téve" üzenet azt sugallná,
    // hogy kétszer annyi munka indult.
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setStatus('sending');
    setProgress(null);

    void api<NavSyncResult>('/nav/sync', { method: 'POST' })
      .then((accepted) => {
        setResult(accepted);
        // NULLA SORBA TETT CÉGNÉL NINCS MIT KÖVETNI. A `done` ág ilyenkor a
        // „nincs bekötött céged" mondatot mondja — egy 0/0-s sáv nem üzenet.
        setStatus(accepted.queued > 0 ? 'running' : 'done');
      })
      .catch((caught: unknown) => {
        const httpStatus = caught instanceof ApiError ? caught.status : 0;

        if (httpStatus === 401) {
          // Nem a művelet hibája: a munkamenet járt le.
          signOut('expired');
          return;
        }

        setStatus(httpStatus === 403 ? 'forbidden' : 'failed');
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [signOut]);

  const since = result?.startedAt ?? null;
  const queued = result?.queued ?? 0;

  /**
   * A KÖVETÉS. Csak `running` alatt él, és a takarítója MINDIG leállítja —
   * `StrictMode` alatt az effekt kétszer fut le, egy elfelejtett `clearTimeout`
   * tehát KÉT időzítőt hagyna hátra, dupla ütemű lekérdezéssel.
   *
   * ⚠️ `setTimeout` LÁNC, NEM `setInterval`. Egy lassú válasz mellett az
   * `setInterval` torlódna: a kérések egymásra futnának, és a sáv a RÉGEBBI
   * válasz szerint ugrálna vissza. Így mindig az előző válasz UTÁN indul a
   * következő várakozás.
   */
  useEffect(() => {
    if (status !== 'running' || since === null) {
      return;
    }

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    let memory: FollowMemory = EMPTY_MEMORY;
    let errors = 0;

    /**
     * EGY FRISS ÁLLAPOT FELDOLGOZÁSA. `true`, ha van még következő kör.
     *
     * ⚠️ KÜLÖN FÜGGVÉNY A HIBAÁGTÓL, és nem csak a Biome kedvéért: a kettő
     * MÁS emlékezetet ír (`memory` kontra `errors`), és összegyúrva az egyik
     * nullázása a másik ágon is lefutna. Egy hálózati koccanás után így nem
     * indulna újra a csend-számláló.
     */
    const onProgress = (next: NavSyncProgress): boolean => {
      errors = 0;
      setProgress(next);

      const step = followStep({
        progress: next,
        queued,
        previous: memory,
        elapsedMs: Date.now() - startedAt,
        quietPolls: QUIET_POLLS,
        maxWaitMs: MAX_WAIT_MS,
        coldWaitMs: COLD_WAIT_MS,
      });
      memory = step.memory;

      if (step.verdict === 'continue') {
        return true;
      }

      setStatus(step.verdict);
      // ⚠️ CSAK A `done` FRISSÍT. A `stalled` azt jelenti, hogy a munka MÉG
      // MEGY a soron; egy frissítés ott félkész állapotot mutatna késznek.
      if (step.verdict === 'done') {
        finished.current?.();
      }
      return false;
    };

    /** EGY KOCCANÁS NEM ÁLLÍTJA LE A KÖVETÉST — a szinkron a szerveren fut. */
    const onError = (caught: unknown): boolean => {
      if (caught instanceof ApiError && caught.status === 401) {
        signOut('expired');
        return false;
      }

      errors += 1;
      if (errors < MAX_POLL_ERRORS) {
        return true;
      }

      setStatus('stalled');
      return false;
    };

    /** A lekérdezés egy köre. `true`, ha van még következő. */
    const poll = async (): Promise<boolean> => {
      try {
        const next = await api<NavSyncProgress>(
          `/nav/sync/status?since=${encodeURIComponent(since)}`
        );
        return stopped ? false : onProgress(next);
      } catch (caught: unknown) {
        return stopped ? false : onError(caught);
      }
    };

    const tick = async () => {
      if (await poll()) {
        timer = setTimeout(() => void tick(), POLL_MS);
      }
    };

    timer = setTimeout(() => void tick(), POLL_MS);

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [status, since, queued, signOut]);

  const dismiss = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setProgress(null);
  }, []);

  const remaining = remainingOf(progress, queued);

  return {
    status,
    result,
    progress,
    remaining,
    etaSeconds: etaSecondsOf(progress, remaining),
    run,
    dismiss,
  };
}
